/**
 * 现场会面表单：新建或编辑「和 sub / utility planner 在现场见」的计划。
 *
 * 需求背景：工程周历原本只能登记 City 市检，约分包商或公用事业公司的 planner
 * 到现场碰头（量尺、交接、定位、确认施工窗口）没有落点。本表单与会面周历卡片共用
 * 同一个 site_meetings 接口，与市检表单并列展示但互不影响。
 *
 * 与市检表单的差异（刻意做得更轻）：
 * - 会面没有「检查范围 / 工序关联 / 希望检查时段」，这些是 City 验收专用的字段；
 * - 会面有具体时刻和预计时长，市检只有上午/下午；
 * - 会面外部单位可从公司库选或手填，市检根本不需要外部单位字段。
 *
 * 外部单位交互取舍：用一个下拉先选「从公司库选」还是「手动填写」，
 * 而不是两个都常驻的输入框 —— 两者同时出现时用户常把公司库里选中的公司名
 * 又手填一遍，导致显示重复。选择手填时才出现名称输入框。
 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

const input = "mt-1 block w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100";
const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2";
const failure = (e) => typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e.message;
const KINDS = [["subcontractor", "分包商", "Subcontractor"], ["utility", "公用事业公司", "Utility"], ["city", "City / 市政", "City"], ["owner", "业主", "Owner"], ["other", "其他", "Other"]];
const PRESETS = [[0, "", "不限"], [30, "30 分钟", "30 min"], [60, "1 小时", "1 hr"], [90, "1.5 小时", "1.5 hr"], [120, "2 小时", "2 hr"], [240, "半天", "Half day"]];

/** day 为日历格子点击时的预填日期；projects 限定可管理的项目范围，服务端仍会重新核验。 */
export default function SiteMeetingForm({ record, day, projectId = "", projects = [], api, zh, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    projectId: record?.projectId || projectId || "",
    kind: record?.kind || "subcontractor",
    title: record?.title || "",
    date: record?.date || day || "",
    startTime: record?.startTime || "",
    durationMinutes: record?.durationMinutes || 60,
    externalMode: record?.companyId ? "library" : (record?.externalName ? "manual" : "library"),
    companyId: record?.companyId || "",
    externalName: record?.externalName || "",
    externalContact: record?.externalContact || "",
    externalPhone: record?.externalPhone || "",
    location: record?.location || "",
    notes: record?.notes || "",
    ownerId: record?.ownerId || "",
    attendeeIds: record?.attendeeIds || [],
  }));
  const [choices, setChoices] = useState({ companies: [], staff: [] });
  const [loading, setLoading] = useState(true), [loadError, setLoadError] = useState("");
  const [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const [staffSearch, setStaffSearch] = useState(""), [companySearch, setCompanySearch] = useState("");
  const [retry, setRetry] = useState(0);
  const requestId = useRef(crypto.randomUUID()), lock = useRef(false);
  // 只在新建时带入默认负责人；编辑历史记录或切换项目不能覆盖人工选择。
  const initialized = useRef(!!record);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true); setLoadError("");
    api.get("/site-meetings/options", { params: form.projectId ? { projectId: form.projectId } : {}, signal: ctl.signal })
      .then(({ data }) => {
        if (ctl.signal.aborted) return;
        setChoices({ companies: data.companies || [], staff: data.staff || [] });
        if (!initialized.current) {
          initialized.current = true;
          // 新建时默认负责人取当前登录同事（服务端回传），避免每次手动挑人；
          // 编辑已有记录不覆盖原负责人。
          const mine = data.defaultOwnerId || "";
          if (mine) setForm((old) => ({ ...old, ownerId: old.ownerId || mine }));
        }
      })
      .catch((e) => { if (!ctl.signal.aborted) setLoadError(failure(e)); })
      .finally(() => { if (!ctl.signal.aborted) setLoading(false); });
    return () => ctl.abort();
  }, [api, form.projectId, retry, record]);

  const field = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const selectedCompany = choices.companies.find((c) => c.id === form.companyId);
  const selectedStaff = (form.attendeeIds || []).map((id) => choices.staff.find((u) => u.username === id) || record?.attendees?.find((p) => p.username === id) || { username: id, name: id });

  /** 选公司库里的公司时，把联系人和电话带进表单；用户仍可覆盖，保存以表单为准。 */
  const pickCompany = (id) => {
    const co = choices.companies.find((c) => c.id === id);
    setForm((old) => ({ ...old, companyId: id, externalName: co?.name || "",
      externalContact: co?.contact || "", externalPhone: co?.phone || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (lock.current || loading || loadError) return;
    if (!form.projectId) { setError(zh ? "请选择项目" : "Select a project"); return; }
    if (!form.title.trim()) { setError(zh ? "请填写会面事由" : "Enter a meeting purpose"); return; }
    if (!form.date) { setError(zh ? "请选择会面日期" : "Select a meeting date"); return; }
    if (form.externalMode === "library" && !form.companyId) { setError(zh ? "请从公司库选择外部单位，或改为手动填写" : "Pick a company or switch to manual entry"); return; }
    if (form.externalMode === "manual" && !form.externalName.trim()) { setError(zh ? "请填写对方公司或部门名称" : "Enter the external party name"); return; }
    if (!form.ownerId) { setError(zh ? "请选择会面负责人" : "Choose a meeting owner"); return; }
    lock.current = true; setBusy(true); setError("");
    try {
      // 手填模式下必须把 companyId 清空，否则后端会优先按公司库里的旧 id 取值，
      // 出现「表单显示手填名称、保存后变回库内旧公司」的错乱。
      const payload = { ...form, companyId: form.externalMode === "library" ? form.companyId : "" };
      delete payload.externalMode;
      const { data } = record
        ? await api.patch(`/site-meetings/${record.id}`, { ...payload, action: "update", version: record.version })
        : await api.post("/site-meetings", { ...payload, requestId: requestId.current });
      // 复用市检的全局刷新事件，周历、项目摘要等已监听该事件的入口会一起更新。
      window.dispatchEvent(new Event("sub-schedule-changed"));
      onSaved(data);
    } catch (e) { setError(failure(e)); }
    finally { lock.current = false; setBusy(false); }
  };

  return <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto scroll-pb-24 sm:max-w-2xl md:scroll-pb-0" data-testid="site-meeting-dialog" closeLabel={zh ? "关闭现场会面表单" : "Close site meeting form"} closeDisabled={busy}>
      <DialogHeader>
        <DialogTitle>{record ? (zh ? "编辑现场会面" : "Edit site meeting") : (zh ? "安排现场会面" : "Schedule site meeting")}</DialogTitle>
        <DialogDescription>{zh ? "登记与分包商或公用事业公司 planner 在现场见面的时间；会面只占周历，不计入市检验收。" : "Track on-site meetings with subcontractor or utility planners. Meetings appear on the weekly calendar but are not city inspections."}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} data-testid="site-meeting-form" className="space-y-4">
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          <label className="block text-xs font-medium">{zh ? "项目 *" : "Project *"}
            <select className={input} required disabled={!!record || !!projectId} value={form.projectId} data-testid="site-meeting-project"
              onChange={(e) => { initialized.current = true; setForm((old) => ({ ...old, projectId: e.target.value })); }}>
              <option value="">{zh ? "请选择项目" : "Select project"}</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.address || p.id}</option>)}
              {/* 编辑场景下 projects 可能只带当前项目，用 record 兜底避免下拉为空。 */}
              {record && !projects.some((p) => p.id === record.projectId) && <option value={record.projectId}>{record.address || record.projectId}</option>}
            </select>
          </label>
          {loading && <p role="status" className="text-xs text-slate-500">{zh ? "正在读取公司库与同事名单…" : "Loading companies and staff…"}</p>}
          {loadError && <div role="alert" className="text-sm text-red-700">{loadError}<button type="button" onClick={() => setRetry((n) => n + 1)} className="ml-2 underline">{zh ? "重试" : "Retry"}</button></div>}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium">{zh ? "会面类型 *" : "Meeting type *"}
              <select className={input} data-testid="site-meeting-kind" value={form.kind} onChange={(e) => field("kind", e.target.value)}>
                {KINDS.map(([k, z, en]) => <option key={k} value={k}>{zh ? z : en}</option>)}
              </select>
            </label>
            <label className="text-xs font-medium">{zh ? "会面事由 *" : "Purpose *"}
              <input className={input} required maxLength={160} data-testid="site-meeting-title" placeholder={zh ? "例如：SCG planner 现场确认接驳位置" : "e.g. Meet SCG planner on site"}
                value={form.title} onChange={(e) => field("title", e.target.value)} />
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium">{zh ? "外部单位 *" : "External party *"}
              <select className={input} data-testid="site-meeting-external-mode" value={form.externalMode}
                onChange={(e) => { const mode = e.target.value; setForm((old) => ({ ...old, externalMode: mode,
                  ...(mode === "manual" ? { companyId: "" } : {}) })); }}>
                <option value="library">{zh ? "从分包商 / 供应商库选择" : "Pick from vendor library"}</option>
                <option value="manual">{zh ? "手动填写（公司库没有时）" : "Enter manually (not in library)"}</option>
              </select>
            </label>
            {form.externalMode === "library"
              ? <label className="text-xs font-medium">{zh ? "选择公司 *" : "Company *"}
                  <input aria-label={zh ? "搜索公司库" : "Search vendor library"} className={input} placeholder={zh ? "输入公司名筛选" : "Filter by company name"}
                    value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} data-testid="site-meeting-company-search" />
                  <select className={`${input} mt-2`} data-testid="site-meeting-company" value={form.companyId} onChange={(e) => pickCompany(e.target.value)}>
                    <option value="">{zh ? "请选择公司" : "Select company"}</option>
                    {choices.companies.filter((c) => c.name.toLowerCase().includes(companySearch.toLowerCase())).map((c) =>
                      <option key={c.id} value={c.id}>{c.name}{c.trade ? ` · ${c.trade}` : ""}</option>)}
                  </select>
                  {!choices.companies.length && !loading && !loadError && <span className="mt-1 block font-normal text-slate-500">{zh ? "公司库暂无可选分包商 / 供应商，请改为手动填写。" : "No vendors in the library; switch to manual entry."}</span>}
                </label>
              : <label className="text-xs font-medium">{zh ? "对方公司 / 部门名称 *" : "External company / department *"}
                  <input className={input} maxLength={160} data-testid="site-meeting-external-name" placeholder={zh ? "例如：SoCalGas / SCG" : "e.g. SoCalGas / SCG"}
                    value={form.externalName} onChange={(e) => field("externalName", e.target.value)} />
                </label>}
          </div>

          {selectedCompany && form.externalMode === "library" && <p className="text-xs text-slate-500">{zh ? "已从公司库带入联系人，可直接修改：" : "Contact pulled from the vendor library; edit as needed: "}{selectedCompany.name}{selectedCompany.contact ? ` · ${selectedCompany.contact}` : ""}{selectedCompany.phone ? ` · ${selectedCompany.phone}` : ""}</p>}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium">{zh ? "对方联系人" : "Their contact"}
              <input className={input} maxLength={120} data-testid="site-meeting-external-contact" value={form.externalContact} onChange={(e) => field("externalContact", e.target.value)} />
            </label>
            <label className="text-xs font-medium">{zh ? "对方电话" : "Their phone"}
              <input className={input} maxLength={60} data-testid="site-meeting-external-phone" value={form.externalPhone} onChange={(e) => field("externalPhone", e.target.value)} />
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-xs font-medium">{zh ? "会面日期 *" : "Date *"}
              <input type="date" required className={input} data-testid="site-meeting-date" value={form.date} onChange={(e) => field("date", e.target.value)} />
            </label>
            <label className="text-xs font-medium">{zh ? "会面时间（选填）" : "Start time (optional)"}
              <input type="time" className={input} data-testid="site-meeting-time" value={form.startTime} onChange={(e) => field("startTime", e.target.value)} />
            </label>
            <label className="text-xs font-medium">{zh ? "预计时长（选填）" : "Duration (optional)"}
              <select className={input} data-testid="site-meeting-duration" value={form.durationMinutes} onChange={(e) => field("durationMinutes", Number(e.target.value))}>
                {PRESETS.map(([v, z, en]) => <option key={v} value={v}>{zh ? z : en}</option>)}
              </select>
            </label>
          </div>
          {/* 只约到天、还没定几点是常态，明确告知可以留空，避免用户以为必须填时间。 */}
          {!form.startTime && <p className="-mt-2 text-xs text-slate-500">{zh ? "时间可以留空，日历上会显示「时间待定」。" : "Leave the time empty to show “Time TBD” on the calendar."}</p>}

          <label className="block text-xs font-medium">{zh ? "现场地址（选填，默认用项目地址）" : "Site address (optional, defaults to project address)"}
            <input className={input} maxLength={240} data-testid="site-meeting-location" value={form.location} onChange={(e) => field("location", e.target.value)} />
          </label>

          <label className="block text-xs font-medium">{zh ? "会面负责人 *" : "Meeting owner *"}
            <select className={input} required data-testid="site-meeting-owner" value={form.ownerId} onChange={(e) => field("ownerId", e.target.value)}>
              <option value="">{zh ? "选择内部同事" : "Choose a staff member"}</option>
              {choices.staff.map((u) => <option key={u.username} value={u.username}>{u.name}</option>)}
              {record?.ownerId && !choices.staff.some((u) => u.username === record.ownerId) && <option value={record.ownerId} disabled>{record.ownerName || record.ownerId} · {zh ? "账号已停用" : "Inactive account"}</option>}
            </select>
          </label>

          <fieldset className="min-w-0">
            <legend className="text-xs font-medium">{zh ? "现场参加人员（可多选）" : "On-site attendees (optional)"}</legend>
            <p className="mt-1 text-xs text-brand-600">{selectedStaff.map((u) => u.name).join("、") || (zh ? "尚未选择" : "None selected")}</p>
            <input aria-label={zh ? "搜索内部同事" : "Search staff"} className={input} placeholder={zh ? "搜索同事姓名" : "Search staff name"} value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} />
            <div className="mt-2 grid max-h-36 gap-1 overflow-y-auto rounded-lg border border-slate-200 p-2 sm:grid-cols-2">
              {choices.staff.filter((u) => `${u.name} ${u.username}`.toLowerCase().includes(staffSearch.toLowerCase())).map((u) =>
                <label key={u.username} className="flex min-h-[44px] items-center gap-2 px-2 text-xs">
                  <input type="checkbox" checked={form.attendeeIds.includes(u.username)} data-testid={`site-meeting-attendee-${u.username}`}
                    onChange={() => field("attendeeIds", form.attendeeIds.includes(u.username) ? form.attendeeIds.filter((n) => n !== u.username) : [...form.attendeeIds, u.username])} />{u.name}
                </label>)}
              {!choices.staff.length && !loading && !loadError && <p className="px-2 py-1 text-xs text-slate-500">{zh ? "暂无可选内部同事" : "No staff available"}</p>}
            </div>
          </fieldset>

          <div className="flex flex-wrap gap-1">{selectedStaff.map((u) => <button type="button" key={u.username} className="min-h-[32px] rounded bg-slate-100 px-2 text-xs text-slate-800" aria-label={`${zh ? "移除参加人员" : "Remove attendee"}: ${u.name}`} onClick={() => field("attendeeIds", form.attendeeIds.filter((n) => n !== u.username))}>{u.name} ×</button>)}</div>

          <label className="block text-xs font-medium">{zh ? "会面要点 / 准备事项" : "Agenda / preparation"}
            <textarea className={input} rows={3} maxLength={4000} data-testid="site-meeting-notes" value={form.notes} onChange={(e) => field("notes", e.target.value)} />
          </label>
        </fieldset>
        {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {/* 手机保留表单内固定操作区，长名单滚动时仍可保存。 */}
        <div className="sticky bottom-0 z-10 flex flex-wrap justify-end gap-2 border-t border-slate-100 bg-white py-3 md:static md:border-0 md:py-0">
          <Button type="button" variant="outline" disabled={busy} className={focusRing} onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button type="submit" data-testid="site-meeting-save" disabled={busy || loading || !!loadError} className={focusRing}>{busy ? (zh ? "保存中…" : "Saving…") : (record ? (zh ? "保存会面" : "Save meeting") : (zh ? "创建会面" : "Create meeting"))}</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}
